import { IStateManagedEntity } from "../../helpers/state-helpers/state-action";
import { removeDuplicates } from "../../helpers/utils/array.util";
import {
  CreateVacancyLesson,
  UpdateVacancyLesson,
  VacancyLesson,
  VacancyLessonDto,
} from "../schedule/lesson";
import { Schedule } from "../schedule/schedule";
import { School } from "../school/school";
import { SubSchool } from "../school/sub-school";
import { Teacher } from "../teacher/teacher";

export type VacancyStatus = "OPEN" | "CLOSE";

export enum AbsenceReason {
  // Holiday = "HOLIDAY",
  // Sickness = "SICKNESS",
  // PrivateEmergency = "PRIVATE_EMERGENCY",
  // Pregnancy = "PREGNANCY",
  // Other = "OTHER",
  // Plannable = "PLANNABLE",
  // NotPlannable = "NOT_PLANNABLE",

  // Temporary fields for beta test
  EIHO = "EIHO",
  HOEF = "HOEF",
  EIPA = "EIPA",
  PAFA = "PAFA",
  PRAB = "PRAB",
  TOBE = "TOBE",
  EIWO = "EIWO",
  MIZI = "MIZI",
  KUSP = "KUSP",
  DIJU = "DIJU",
  ELUR = "ELUR",
  KOSA = "KOSA",
  WEBI = "WEBI",
  UGAL = "UGAL",
  KEHT = "KEHT",
}

export type VacancyDto = {
  id: string;
  scheduleId: string;
  absentTeacherEmail: string;
  startDate: Date;
  endDate: Date;
  schoolID: string;
  lessons: VacancyLessonDto[];
  description?: string;
  status: VacancyStatus;
  absenceReason: AbsenceReason;
  teacherApplications?: string[];
  teacherAccepted?: string[];
  teacherDeclined?: string[];
};

export class Vacancy implements IStateManagedEntity {
  constructor(
    public id: string,
    public school: School,
    public schedule: Schedule,
    public absentTeacher: Teacher,
    public startDate: Date,
    public endDate: Date,
    public lessons: VacancyLesson[],
    public status: VacancyStatus,
    public absenceReason: AbsenceReason,
    public description?: string,
    public teacherApplications?: Teacher[],
    public teacherAccepted?: Teacher[],
    public teacherDeclined?: Teacher[],
  ) {}

  get subSchools(): SubSchool[] {
    return removeDuplicates(
      this.lessons.map((l) => l.subSchool),
      "subSchoolId",
    );
  }

  static createFromVacancyDto(
    dto: VacancyDto,
    absentTeacher: Teacher,
    schoolTeachers: Teacher[],
    schedule: Schedule,
    school: School,
  ): Vacancy {
    return new Vacancy(
      dto.id,
      school,
      schedule,
      absentTeacher,
      new Date(dto.startDate),
      new Date(dto.endDate),
      dto.lessons.map((l) =>
        VacancyLesson.fromVacancyLessonDto(
          l,
          schoolTeachers,
          school.subSchools.find((ss) => ss.subSchoolId === l.subSchoolId)!,
          schedule.lessons.find((sl) => sl.id === l.lessonId)?.className ?? "",
        ),
      ),
      dto.status,
      dto.absenceReason,
      dto.description,
      dto.teacherApplications
        ? (dto.teacherApplications
            // Filter out teacher applications that are already accepted
            .filter(
              (application) => !dto.teacherAccepted?.includes(application),
            )
            .map((id) => schoolTeachers.find((t) => id === t.id))
            .filter((t) => !!t) as Teacher[])
        : undefined,
      dto.teacherAccepted
        ? (dto.teacherAccepted
            .map((id) => schoolTeachers.find((t) => id === t.id))
            .filter((t) => !!t) as Teacher[])
        : undefined,
      dto.teacherDeclined
        ? (dto.teacherDeclined
            .map((id) => schoolTeachers.find((t) => id === t.id))
            .filter((t) => !!t) as Teacher[])
        : undefined,
    );
  }

  static toUpdateDto(vacancy: Vacancy): UpdateVacancyDto {
    return {
      vacancyId: vacancy.id,
      lessons: vacancy.lessons.map(VacancyLesson.toUpdateVacancyLesson),
      absentTeacherEmail: vacancy.absentTeacher.id,
      description: vacancy.description,
      start: vacancy.startDate,
      end: vacancy.endDate,
      absenceReason: vacancy.absenceReason,
    };
  }
}

export type CreateVacancy = {
  absentTeacherEmail: string;
  scheduleId: string;
  lessons: CreateVacancyLesson[];
  description?: string;
  start: Date;
  end: Date;
  absenceReason: AbsenceReason;
};

export type UpdateVacancyDto = {
  vacancyId: string;
  lessons: UpdateVacancyLesson[];
  absentTeacherEmail: string;
  description?: string;
  start: Date;
  end: Date;
  absenceReason: AbsenceReason;
};

export const absenceReasonMapper = (absenceReason: AbsenceReason) => {
  // switch (absenceReason) {
  //   case AbsenceReason.Holiday:
  //     return "Urlaub";
  //   case AbsenceReason.Sickness:
  //     return "Erkrankung";
  //   case AbsenceReason.PrivateEmergency:
  //     return "Privater Notfall";
  //   case AbsenceReason.Pregnancy:
  //     return "Schwangerschaft";
  //   case AbsenceReason.Other:
  //     return "Sonstiges";
  //   case AbsenceReason.Plannable:
  //     return "Planbar";
  //   case AbsenceReason.NotPlannable:
  //     return "Nicht planbar";
  //   default:
  //     throw Error("Absence reason not known");
  // }
  return absenceReason;
};
